import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  REQUIRED_VEHICLE_IMAGE_KINDS,
  vehicleDocumentRequirements,
  vehiclePaperworkReady,
  type DocumentRequirement,
  type ListingStatus,
  type PartnerDocument,
  type Vehicle,
} from '@zuruflex/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  FormField,
  PageHeader,
  Separator,
  Skeleton,
  Textarea,
  VehicleStatusChip,
} from '@zuruflex/ui';
import { longHireDiscountRate, quoteBooking } from '@zuruflex/core';
import { formatDateIn, formatMoneyIn, toLocale } from '@zuruflex/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, Check, ChevronLeft, ExternalLink, ImageOff, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { AvailabilityCheck } from '../components/AvailabilityCheck.js';
import { VehicleOwnerCard } from '../components/CarOwnersCard.js';
import { ChangeOwnerDialog } from '../components/ChangeOwnerDialog.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const LISTING_VARIANT: Record<
  ListingStatus,
  'success' | 'info' | 'danger' | 'default' | 'outline'
> = {
  PUBLISHED: 'success',
  IN_REVIEW: 'info',
  REJECTED: 'danger',
  DRAFT: 'default',
  UNLISTED: 'outline',
};

const DOCUMENT_VARIANT = {
  MISSING: 'outline',
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'danger',
} as const;

/**
 * One car, and where its listing is reviewed.
 *
 * The review is of the whole car — what it is, what it looks like, what it
 * costs and what covers it — so the decision sits on the page that shows
 * all four. Its certificates used to be approved one at a time on the
 * partner's page, a screen about the account, which approved an insurance
 * policy without the car it insures in view.
 */
export function VehicleDetailPage() {
  const { idOrSlug = '' } = useParams();
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);
  const { user } = useAuth();
  // The API's bar for a transfer; below it the button would only meet a 403.
  const canTransfer = user?.role === 'OWNER' || user?.role === 'MANAGER';
  const [changingOwner, setChangingOwner] = useState(false);

  const {
    data: vehicle,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ['vehicle', idOrSlug],
    queryFn: () => api.getVehicle(idOrSlug),
  });

  const documents = useQuery({
    // Keyed on the owner too: the documents are fetched through the partner,
    // and a car handed to another one must not reread the old owner's list.
    queryKey: ['vehicle-paperwork', vehicle?.id, vehicle?.partnerId],
    queryFn: () => api.partnerDocuments(vehicle!.partnerId, vehicle!.id),
    enabled: Boolean(vehicle),
  });

  // Same key as the listing review card's, so this costs no second request.
  const partner = useQuery({
    queryKey: ['partner', vehicle?.partnerId],
    queryFn: () => api.getPartner(vehicle!.partnerId),
    enabled: Boolean(vehicle),
  });

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  if (isPending) return <Skeleton className="h-96 w-full" />;

  const spec = [
    [t('common:field.class'), t(`common:vehicleClass.${vehicle.vehicleClass}`)],
    [t('common:field.transmission'), t(`common:transmission.${vehicle.transmission}`)],
    [t('common:field.fuel'), t(`common:fuelType.${vehicle.fuelType}`)],
    [t('common:field.seats'), String(vehicle.seats)],
    [t('common:field.doors'), String(vehicle.doors)],
    [t('common:field.colour'), vehicle.colour ?? '—'],
    [t('common:field.plate'), vehicle.plateNumber],
    [t('common:field.odometer'), `${vehicle.odometerKm.toLocaleString(locale)} km`],
    [
      t('common:field.kmAllowance'),
      vehicle.dailyKmAllowance
        ? t('common:unit.kmPerDay', { count: vehicle.dailyKmAllowance })
        : t('common:unit.unlimited'),
    ],
    [t('common:field.driver'), t(`common:driverPolicy.${vehicle.driverPolicy}`)],
  ] as const;

  /*
   * Long-hire prices come from `quoteBooking` rather than from arithmetic
   * repeated here. The panel used to multiply the daily rate by seven and
   * take ten percent off, which was a second copy of the discount policy and
   * is wrong twice over on a car let with a driver: wrong base, and the
   * driver's day rate is not discounted at all.
   *
   * It still reads the built-in bands rather than the branch's own settings,
   * exactly as the hard-coded version did — so this is indicative, and the
   * quote endpoint remains the number anybody is charged.
   */
  const band = (days: number) => {
    const pickupAt = new Date('2026-01-01T09:00:00.000Z');
    const dropoffAt = new Date(pickupAt.getTime() + days * 86_400_000);
    const quote = quoteBooking({
      dailyRate: vehicle.dailyRate,
      currency: vehicle.currency,
      pickupAt,
      dropoffAt,
      ...(vehicle.driverPolicy === 'DRIVER_ONLY' && vehicle.driverDailyRate
        ? { driverDailyRate: vehicle.driverDailyRate }
        : {}),
    });
    return {
      percent: Math.round(longHireDiscountRate(days) * 100),
      total: formatMoneyIn(quote.subtotal, vehicle.currency, locale),
    };
  };
  const weekly = band(7);
  const monthly = band(28);

  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/fleet"
            className="inline-flex items-center gap-1 text-sm text-text-muted transition-colors hover:text-text"
          >
            <ChevronLeft className="size-4" />
            {t('vehicle.backToFleet')}
          </Link>
        }
        title={`${vehicle.make} ${vehicle.model}`}
        description={`${vehicle.year} · ${vehicle.slug}`}
        action={
          <div className="flex items-center gap-2">
            <Badge variant={LISTING_VARIANT[vehicle.listingStatus]}>
              {t(`common:listingStatus.${vehicle.listingStatus}`)}
            </Badge>
            <VehicleStatusChip
              status={vehicle.status}
              label={t(`common:vehicleStatus.${vehicle.status}`)}
            />
            {canTransfer && vehicle.status !== 'RETIRED' ? (
              <Button variant="outline" size="sm" onClick={() => setChangingOwner(true)}>
                <ArrowLeftRight className="size-4" />
                {t('vehicle.changeOwner')}
              </Button>
            ) : null}
          </div>
        }
      />

      {changingOwner ? (
        <ChangeOwnerDialog vehicle={vehicle} onClose={() => setChangingOwner(false)} />
      ) : null}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <CardHeader>
              <div>
                <CardTitle>{t('vehicle.photos')}</CardTitle>
                <CardDescription>
                  {t('vehicle.photosCount', { count: vehicle.images.length })}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {vehicle.images.length === 0 ? (
                <div className="flex h-56 flex-col items-center justify-center gap-2 rounded-card bg-surface-sunken text-text-muted">
                  <ImageOff className="size-5" />
                  <p className="text-sm">{t('vehicle.noPhotos')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {vehicle.images.map((image) => (
                    <figure key={image.id}>
                      <img
                        src={image.url}
                        alt={image.alt ?? `${vehicle.make} ${vehicle.model}`}
                        className="aspect-[4/3] w-full rounded-card object-cover"
                      />
                      {/* Review is checking a set, not a pile: the caption is
                          how a reviewer sees that the cabin shot is missing. */}
                      <figcaption className="mt-1.5 text-xs text-text-muted">
                        {t(`common:vehicleImageKind.${image.kind}`)}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <PaperworkCard
            requirements={vehicleDocumentRequirements(documents.data ?? [], vehicle.id)}
            isPending={documents.isPending}
          />

          <Card>
            <CardHeader>
              <CardTitle>{t('vehicle.specification')}</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
                {spec.map(([label, value]) => (
                  <div key={label}>
                    <dt className="text-[11px] font-bold uppercase tracking-widest text-text-muted">
                      {label}
                    </dt>
                    <dd className="mt-1 text-sm font-medium">{value}</dd>
                  </div>
                ))}
              </dl>

              {vehicle.features.length > 0 ? (
                <>
                  <Separator className="my-5" />
                  <div className="flex flex-wrap gap-2">
                    {vehicle.features.map((feature) => (
                      <Badge key={feature}>{feature}</Badge>
                    ))}
                  </div>
                </>
              ) : null}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <ListingReviewCard vehicle={vehicle} documents={documents.data} />

          {partner.data?.isFirstParty ? (
            <VehicleOwnerCard partnerId={vehicle.partnerId} vehicleId={vehicle.id} />
          ) : null}

          <Card variant="elevated">
            <CardHeader>
              <CardTitle>{t('vehicle.pricing')}</CardTitle>
            </CardHeader>
            <CardContent>
              {/* The headline is what the car costs to book. For a car that
                  cannot be hired without a driver that includes one, which is
                  what the fleet grid and the price filters promise. */}
              <p className="font-display text-4xl font-bold">
                {formatMoneyIn(vehicle.effectiveDailyRate, vehicle.currency, locale)}
              </p>
              <p className="text-xs text-text-muted">{t('common:unit.perDay')}</p>

              <Separator className="my-4" />

              <dl className="space-y-2.5 text-sm">
                {vehicle.driverPolicy === 'SELF_DRIVE_ONLY' ? null : (
                  <>
                    {/* Named separately so it is clear which part of the
                        headline is the car and which is the driver. */}
                    <Row
                      label={t('vehicle.baseRate')}
                      value={formatMoneyIn(vehicle.dailyRate, vehicle.currency, locale)}
                    />
                    <Row
                      label={t('common:field.driverRate')}
                      value={formatMoneyIn(vehicle.driverDailyRate ?? 0, vehicle.currency, locale)}
                    />
                  </>
                )}
                <Row
                  label={t('vehicle.weekly', { percent: weekly.percent })}
                  value={weekly.total}
                />
                <Row
                  label={t('vehicle.monthly', { percent: monthly.percent })}
                  value={monthly.total}
                />
                <Row
                  label={t('common:field.deposit')}
                  value={formatMoneyIn(vehicle.depositAmount ?? 0, vehicle.currency, locale)}
                />
              </dl>
            </CardContent>
          </Card>

          <AvailabilityCheck vehicleId={vehicle.id} />

          {vehicle.description ? (
            <Card>
              <CardHeader>
                <CardTitle>{t('vehicle.description')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-text-muted">{vehicle.description}</p>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-text-muted">{label}</dt>
      <dd className="font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

// ── The car's certificates ───────────────────────────────────

/**
 * What covers the car, newest of each kind. No buttons: these are decided
 * with the listing, not one by one.
 */
function PaperworkCard({
  requirements,
  isPending,
}: {
  requirements: DocumentRequirement<PartnerDocument>[];
  isPending: boolean;
}) {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const locale = toLocale(i18n.language);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('vehicle.paperwork')}</CardTitle>
          <CardDescription>{t('vehicle.paperworkHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        {isPending ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <ul className="flex flex-col gap-2">
            {requirements.map(({ kind, state, document }) => (
              <li
                key={kind}
                className="flex flex-wrap items-center justify-between gap-3 rounded-field border border-border-default px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{t(`common:documentKind.${kind}`)}</p>
                  <p className="truncate text-xs text-text-muted">
                    {document
                      ? [
                          document.number,
                          document.expiresAt
                            ? t('partners.expires', {
                                date: formatDateIn(document.expiresAt, locale),
                              })
                            : null,
                          document.reviewedBy
                            ? t('partners.reviewedBy', { name: document.reviewedBy })
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || t('partners.noDetails')
                      : t('partners.neverFiled')}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Badge variant={DOCUMENT_VARIANT[state]}>
                    {t(`common:documentState.${state}`)}
                  </Badge>
                  {document ? (
                    <Button asChild size="sm" variant="ghost">
                      <a href={document.url} target="_blank" rel="noreferrer">
                        <ExternalLink className="size-3.5" />
                        <span className="ml-1">{t('partners.view')}</span>
                      </a>
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

// ── The decision ─────────────────────────────────────────────

/**
 * Publish or send back, for the whole car.
 *
 * The checklist is the API's own gate read in advance, so nobody presses
 * Publish to be told the roadworthy is missing. Publishing approves the
 * paperwork with the car; sending back needs a note, which the partner is
 * shown word for word.
 */
function ListingReviewCard({
  vehicle,
  documents,
}: {
  vehicle: Vehicle;
  documents: PartnerDocument[] | undefined;
}) {
  const { t } = useTranslation(['admin', 'common']);
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const partner = useQuery({
    queryKey: ['partner', vehicle.partnerId],
    queryFn: () => api.getPartner(vehicle.partnerId),
  });

  const decide = useMutation({
    mutationFn: (status: 'PUBLISHED' | 'REJECTED') =>
      api.reviewListing(vehicle.id, {
        status,
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async () => {
      setRejecting(false);
      setNote('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicle'] }),
        queryClient.invalidateQueries({ queryKey: ['vehicle-paperwork', vehicle.id] }),
        queryClient.invalidateQueries({ queryKey: ['fleet'] }),
      ]);
    },
  });

  const have = new Set(vehicle.images.map((image) => image.kind));
  const checks = [
    ['vehicle.check.photos', REQUIRED_VEHICLE_IMAGE_KINDS.every((kind) => have.has(kind))],
    [
      'vehicle.check.price',
      vehicle.dailyRate > 0 &&
        (vehicle.driverPolicy === 'SELF_DRIVE_ONLY' || (vehicle.driverDailyRate ?? 0) > 0),
    ],
    [
      'vehicle.check.paperwork',
      documents !== undefined &&
        vehiclePaperworkReady(vehicleDocumentRequirements(documents, vehicle.id)),
    ],
  ] as const;
  const ready = checks.every(([, ok]) => ok);
  const waiting = vehicle.listingStatus === 'IN_REVIEW';

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t('vehicle.review')}</CardTitle>
          <CardDescription>{t('vehicle.reviewHint')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {partner.data ? (
          <p className="text-sm">
            <span className="text-text-muted">{t('vehicle.owner')} </span>
            <Link to={`/partners/${partner.data.id}`} className="font-medium hover:underline">
              {partner.data.displayName}
            </Link>
          </p>
        ) : null}

        {partner.data && partner.data.status !== 'APPROVED' ? (
          <Alert variant="warning">{t('vehicle.ownerNotApproved')}</Alert>
        ) : null}

        <ul className="space-y-1.5">
          {checks.map(([key, ok]) => (
            <li key={key} className="flex items-center gap-2 text-sm">
              {ok ? (
                <Check className="size-4 text-success" />
              ) : (
                <X className="size-4 text-danger" />
              )}
              <span className={ok ? undefined : 'text-text-muted'}>{t(key)}</span>
            </li>
          ))}
        </ul>

        {vehicle.listingStatus === 'REJECTED' && vehicle.reviewNote ? (
          <Alert variant="danger">{t('vehicle.sentBackWith', { note: vehicle.reviewNote })}</Alert>
        ) : null}

        {decide.error ? (
          <Alert variant="danger">
            {decide.error instanceof ZuruflexApiError
              ? decide.error.message
              : t('common:error.unexpected')}
          </Alert>
        ) : null}

        {waiting ? (
          <>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!ready || decide.isPending}
                onClick={() => decide.mutate('PUBLISHED')}
              >
                {t('vehicle.publish')}
              </Button>
              <Button variant="ghost" onClick={() => setRejecting((open) => !open)}>
                {t('vehicle.sendBack')}
              </Button>
            </div>

            {rejecting ? (
              <div>
                <FormField label={t('vehicle.sendBackNote')} required>
                  {(field) => (
                    <Textarea
                      {...field}
                      rows={3}
                      value={note}
                      onChange={(event) => setNote(event.target.value)}
                      placeholder={t('vehicle.sendBackPlaceholder')}
                    />
                  )}
                </FormField>
                <Button
                  size="sm"
                  variant="destructive"
                  className="mt-2"
                  disabled={note.trim().length < 3 || decide.isPending}
                  onClick={() => decide.mutate('REJECTED')}
                >
                  {t('vehicle.sendBack')}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-xs text-text-muted">
            {t('vehicle.notWaiting', {
              status: t(`common:listingStatus.${vehicle.listingStatus}`),
            })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
